import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { FaCheck, FaClock, FaEnvelope, FaHome, FaPhoneAlt, FaSignInAlt } from 'react-icons/fa';
import { fadeIn, fadeInUp, scaleIn, reducedMotion } from './partnerSignupAnimations';
import { scrollSignupPanelToTop } from './partnerSignupBusiness';
import { partnerSignupThemeVars } from './partnerSignupTheme';

const ringPulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(0, 174, 239, 0.35); }
  50% { box-shadow: 0 0 0 14px rgba(0, 174, 239, 0); }
`;

const checkPop = keyframes`
  0% { transform: scale(0.6); opacity: 0; }
  60% { transform: scale(1.08); }
  100% { transform: scale(1); opacity: 1; }
`;

const STEPS = [
  { title: 'Document review', desc: 'Our team verifies your business details and uploads.' },
  { title: 'We reach out', desc: 'You will get a call or email on your registered contact.' },
  { title: 'Go live', desc: 'Once approved, your seller account is activated on Sparepe.' },
];

function readStoredUserContact() {
  try {
    const raw = localStorage.getItem('user');
    if (!raw) return { phone: '', email: '' };
    const user = JSON.parse(raw);
    return {
      phone: String(user?.phone || '').replace(/\D/g, '').slice(-10),
      email: String(user?.email || '').trim(),
    };
  } catch {
    return { phone: '', email: '' };
  }
}

function formatDisplayPhone(digits) {
  const d = String(digits || '').replace(/\D/g, '').slice(-10);
  if (d.length !== 10) return d ? `+91 ${d}` : '';
  return `+91 ${d.slice(0, 5)} ${d.slice(5)}`;
}

export default function PartnerSignupPending() {
  const navigate = useNavigate();
  const [partnerId, setPartnerId] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');

  useEffect(() => {
    const fromUser = readStoredUserContact();
    setPartnerId(localStorage.getItem('partner_signup_partner_id') || '');
    setPhone(
      String(localStorage.getItem('partner_signup_phone') || fromUser.phone || '')
        .replace(/\D/g, '')
        .slice(-10)
    );
    setEmail(
      (localStorage.getItem('partner_signup_email') || fromUser.email || '').trim()
    );
    scrollSignupPanelToTop('partner-pending-top');
  }, []);

  return (
    <Page className="auth-light-page partner-signup-pending">
      <BgOrb $pos="top" />
      <BgOrb $pos="bottom" />

      <Shell>
        <Card>
          <Logo id="partner-pending-top" src="/sparepe.jpeg" alt="Sparepe" decoding="async" />

          <IconRing>
            <IconInner>
              <FaCheck />
            </IconInner>
          </IconRing>

          <Badge>Application submitted</Badge>
          <Title>Thank you for joining Sparepe</Title>
          <Lead>
            We have received your registration. Sit back — our team is reviewing your application.
          </Lead>

          {partnerId && (
            <PartnerIdCard>
              <PartnerIdLabel>Your seller ID</PartnerIdLabel>
              <PartnerIdValue data-pp-id-value>{partnerId}</PartnerIdValue>
              <PartnerIdHint>Save this ID — you may need it when we contact you.</PartnerIdHint>
            </PartnerIdCard>
          )}

          <WaitCard>
            <WaitIcon><FaClock /></WaitIcon>
            <WaitBody>
              <WaitTitle>Verification in progress</WaitTitle>
              <WaitText>
                Please allow <strong>24 to 72 hours</strong>. We will connect with you on your
                registered phone number or email once the review is complete.
              </WaitText>
            </WaitBody>
          </WaitCard>

          <StepsSection>
            <StepsHeading>What happens next</StepsHeading>
            <StepsList>
              {STEPS.map((step, i) => (
                <StepItem key={step.title}>
                  <StepNum>{i + 1}</StepNum>
                  <StepContent>
                    <StepTitle>{step.title}</StepTitle>
                    <StepDesc>{step.desc}</StepDesc>
                  </StepContent>
                </StepItem>
              ))}
            </StepsList>
          </StepsSection>

          <TipRow>
            {phone ? (
              <TipChip data-pp-contact>
                <FaPhoneAlt />
                <TipChipText>
                  <TipChipLabel data-pp-contact-label>Mobile</TipChipLabel>
                  <TipChipValue data-pp-contact>{formatDisplayPhone(phone)}</TipChipValue>
                </TipChipText>
              </TipChip>
            ) : (
              <TipChip data-pp-contact><FaPhoneAlt /> Keep phone handy</TipChip>
            )}
            {email ? (
              <TipChip data-pp-contact>
                <FaEnvelope />
                <TipChipText>
                  <TipChipLabel data-pp-contact-label>Email</TipChipLabel>
                  <TipChipValue data-pp-contact>{email}</TipChipValue>
                </TipChipText>
              </TipChip>
            ) : (
              <TipChip data-pp-contact><FaEnvelope /> Check your inbox</TipChip>
            )}
          </TipRow>

          <Actions>
            <PrimaryBtn type="button" onClick={() => navigate('/')}>
              <FaHome /> Back to home
            </PrimaryBtn>
            <SecondaryBtn type="button" onClick={() => navigate('/login')}>
              <FaSignInAlt /> Sign in to check status
            </SecondaryBtn>
          </Actions>
        </Card>
      </Shell>
    </Page>
  );
}

const Page = styled.div`
  ${partnerSignupThemeVars}
  --pp-brand-dark: #0090c8;
  --pp-radius-card: 16px;
  --pp-radius-btn: 10px;

  position: relative;
  width: 100%;
  min-height: 100vh;
  min-height: 100dvh;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow-x: hidden;
  background: linear-gradient(160deg, #f0f9ff 0%, #f8fafc 45%, #ecfeff 100%);
  color: var(--pp-text);
  animation: ${fadeIn} 0.45s ease;
  ${reducedMotion}

  html.dark &,
  body.app-dark-theme & {
    background: linear-gradient(
      160deg,
      var(--sp-bg-deep, #060a10) 0%,
      var(--sp-bg, #0d1117) 45%,
      #0a1622 100%
    );
  }

  @media (max-width: 820px) {
    align-items: flex-start;
  }
`;

const BgOrb = styled.div`
  position: absolute;
  width: min(420px, 90vw);
  height: min(420px, 90vw);
  border-radius: 50%;
  background: radial-gradient(circle, rgba(0, 174, 239, 0.12) 0%, transparent 70%);
  pointer-events: none;
  ${({ $pos }) => $pos === 'top'
    ? 'top: -120px; right: -80px;'
    : 'bottom: -140px; left: -100px;'}
`;

const Shell = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: min(440px, calc(100vw - 32px));
  margin: 0 auto;
  padding: 20px 16px 28px;
  box-sizing: border-box;

  @media (min-width: 821px) {
    padding: 32px 24px 40px;
  }
`;

const Card = styled.div`
  width: 100%;
  padding: 28px 22px 24px;
  background: var(--pp-surface);
  border: 1px solid rgba(226, 232, 240, 0.9);
  border-radius: var(--pp-radius-card);
  box-shadow:
    0 4px 6px -1px rgba(15, 23, 42, 0.06),
    0 20px 40px -12px rgba(0, 174, 239, 0.12);
  text-align: center;
  animation: ${scaleIn} 0.5s cubic-bezier(0.22, 1, 0.36, 1);
  ${reducedMotion}

  @media (min-width: 821px) {
    padding: 36px 32px 30px;
  }
`;

const Logo = styled.img`
  width: 56px;
  height: 56px;
  object-fit: contain;
  margin: 0 auto 20px;
  border-radius: 12px;

  @media (min-width: 480px) {
    width: 64px;
    height: 64px;
  }
`;

const IconRing = styled.div`
  width: 80px;
  height: 80px;
  margin: 0 auto 18px;
  border-radius: 50%;
  background: var(--pp-brand-bg);
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${ringPulse} 2.5s ease-in-out infinite;
  ${reducedMotion}
`;

const IconInner = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--pp-brand) 0%, var(--pp-brand-dark) 100%);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 26px;
  animation: ${checkPop} 0.55s cubic-bezier(0.22, 1, 0.36, 1) 0.15s both;
  ${reducedMotion}
`;

const Badge = styled.span`
  display: inline-block;
  padding: 6px 14px;
  margin-bottom: 12px;
  border-radius: 999px;
  background: var(--pp-brand-bg);
  color: var(--pp-brand-dark);
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  animation: ${fadeInUp} 0.45s ease 0.1s both;
  ${reducedMotion}
`;

const Title = styled.h1`
  margin: 0 0 10px;
  font-size: clamp(22px, 5vw, 26px);
  font-weight: 700;
  color: var(--pp-text);
  line-height: 1.25;
  animation: ${fadeInUp} 0.45s ease 0.15s both;
  ${reducedMotion}
`;

const Lead = styled.p`
  margin: 0 0 22px;
  font-size: clamp(14px, 3.5vw, 15px);
  color: var(--pp-muted);
  line-height: 1.6;
  animation: ${fadeInUp} 0.45s ease 0.2s both;
  ${reducedMotion}
`;

const PartnerIdCard = styled.div`
  margin: 0 0 20px;
  padding: 14px 16px;
  border-radius: var(--pp-radius-btn);
  background: var(--pp-success-bg);
  border: 1px solid var(--pp-success-border);
  animation: ${fadeInUp} 0.45s ease 0.22s both;
  ${reducedMotion}
`;

const PartnerIdLabel = styled.p`
  margin: 0 0 4px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--pp-success-text);
`;

const PartnerIdValue = styled.p`
  margin: 0;
  font-size: clamp(22px, 5vw, 28px);
  font-weight: 700;
  letter-spacing: 0.08em;
  color: var(--pp-text);
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
`;

const PartnerIdHint = styled.p`
  margin: 6px 0 0;
  font-size: 12px;
  color: var(--pp-muted);
  line-height: 1.4;
`;

const WaitCard = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  text-align: left;
  padding: 16px;
  margin-bottom: 22px;
  border-radius: var(--pp-radius-btn);
  background: var(--pp-brand-bg);
  border: 1px solid var(--pp-brand-border);
  animation: ${fadeInUp} 0.45s ease 0.25s both;
  ${reducedMotion}
`;

const WaitIcon = styled.div`
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: var(--pp-surface);
  color: var(--pp-brand);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  box-shadow: 0 2px 8px rgba(0, 174, 239, 0.15);
`;

const WaitBody = styled.div`
  min-width: 0;
`;

const WaitTitle = styled.p`
  margin: 0 0 6px;
  font-size: 14px;
  font-weight: 600;
  color: var(--pp-text);
`;

const WaitText = styled.p`
  margin: 0;
  font-size: 13px;
  color: var(--pp-muted);
  line-height: 1.55;

  strong {
    color: var(--pp-brand-dark);
    font-weight: 700;
  }
`;

const StepsSection = styled.div`
  margin-bottom: 20px;
  text-align: left;
  animation: ${fadeInUp} 0.45s ease 0.3s both;
  ${reducedMotion}
`;

const StepsHeading = styled.h2`
  margin: 0 0 14px;
  font-size: 13px;
  font-weight: 600;
  color: var(--pp-muted);
  text-transform: uppercase;
  letter-spacing: 0.06em;
`;

const StepsList = styled.ol`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const StepItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 12px;
`;

const StepNum = styled.span`
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: var(--pp-brand-bg);
  color: var(--pp-brand-dark);
  font-size: 13px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StepContent = styled.div`
  min-width: 0;
  padding-top: 2px;
`;

const StepTitle = styled.p`
  margin: 0 0 2px;
  font-size: 14px;
  font-weight: 600;
  color: var(--pp-text);
`;

const StepDesc = styled.p`
  margin: 0;
  font-size: 13px;
  color: var(--pp-muted);
  line-height: 1.45;
`;

const TipRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
  margin-bottom: 22px;
  animation: ${fadeInUp} 0.45s ease 0.35s both;
  ${reducedMotion}
`;

const TipChip = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  gap: 10px;
  padding: 10px 14px;
  border-radius: 12px;
  background: var(--pp-input-muted-bg);
  border: 1px solid var(--pp-border);
  font-size: 13px;
  color: var(--pp-text);
  text-align: left;

  > svg {
    flex-shrink: 0;
    color: var(--pp-brand);
    font-size: 14px;
  }
`;

const TipChipText = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const TipChipLabel = styled.span`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--pp-muted);
`;

const TipChipValue = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: var(--pp-text);
  word-break: break-word;
`;

const Actions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  animation: ${fadeInUp} 0.45s ease 0.4s both;
  ${reducedMotion}
`;

const PrimaryBtn = styled.button`
  width: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 48px;
  padding: 12px 18px;
  border: none;
  border-radius: var(--pp-radius-btn);
  background: linear-gradient(135deg, var(--pp-brand) 0%, var(--pp-brand-dark) 100%);
  color: #fff;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 4px 14px rgba(0, 174, 239, 0.35);
  transition: transform 0.15s ease, box-shadow 0.15s ease;

  &:active {
    transform: scale(0.98);
  }
`;

const SecondaryBtn = styled.button`
  width: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 48px;
  padding: 12px 18px;
  border: 1px solid var(--pp-border);
  border-radius: var(--pp-radius-btn);
  background: var(--pp-surface);
  color: var(--pp-text);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease;

  &:active {
    background: #f8fafc;
    border-color: var(--pp-brand);
  }
`;
